<script lang="ts">
	/**
	 * `/Migration` — moving the legacy store's customers here (§13).
	 *
	 * **A sequence, and it says which step it is on.** Upload → Map → Dry run → Apply → Records.
	 * This is a procedure somebody runs once, under pressure, against real money, and the steps are
	 * not interchangeable: the dry run is what makes Apply safe, and Apply is the one irreversible
	 * thing in the whole feature. A screen that offered all five as equal buttons would let an
	 * operator press the last one first.
	 *
	 * Three things on it are decisions rather than layout.
	 *
	 * **Every panel states how many decisions are outstanding *and how many of those are
	 * blocking*.** "41 unmapped products" means nothing on its own — what matters is that three of
	 * them carry live subscriptions and the rest are archived lines nobody will miss.
	 *
	 * **Apply says what it will not do.** It will not charge anybody and it will not notify any
	 * application, and both are worth saying out loud on the button that materialises a customer
	 * base: the fear that stops an operator pressing it is exactly those two things.
	 *
	 * **The records table stays afterwards.** It is the answer to every question this migration
	 * generates for the next two years — where did this transaction go, was this customer imported
	 * at all — and a screen that cleared itself on success would throw that away at the moment it
	 * started being useful.
	 */

	import { onDestroy, onMount } from 'svelte';
	import { Badge, Button, Spinner, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, PageHeader, Panel, PromptButton, RoutedLink } from '../../components';
	import MigrationPlan from './MigrationPlan.svelte';
	import {
		applyMigrationBatch,
		createMigrationProducts,
		decideMigrationMappings,
		discardMigrationBatch,
		revertMigrationBatch,
		notifyMigrationApplications,
		getMigrationBatch,
		listMigrationBatches,
		listMigrationProposals,
		listMigrationMappingTargets,
		lookupMigrationRecord,
		runMigrationDryRun,
		readMigrationDryRun,
		uploadMigrationExport,
		uploadMigrationDocuments,
		readMigrationDocumentImport,
		createCurrency,
		updateProduct,
		listMigrationDryRunFindings,
		type MigrationFinding,
		type MigrationFindingSeverity,
		type MigrationQuickFix,
		type MigrationApplySummary,
		type MigrationDocumentImportStatus,
		type MigrationBatchSummary,
		type MigrationCreatedProduct,
		type MigrationDecision,
		type MigrationDryRunReport,
		type MigrationDryRunStatus,
		type MigrationLookupResult,
		type MigrationMappingTarget,
		type MigrationProposal,
		type MigrationNotifySummary,
		type MigrationRevertSummary
	} from '../../AppNetwork';
	import { listPaymentProviders } from '../../AppNetwork';
	import type { PaymentProviderConfig } from '../../AppTypes';
	import { toastError, toastSuccess } from '../../stores/toast';
	import { majorUnitsFrom, minorUnitsFrom } from '../../Utilities';
	import { conditionOf } from '../../AppNetwork';

	/** The mapping panels, in the order an admin works through them. */
	const MAPPING_KINDS: { kind: string; label: string; help: string }[] = [
		{
			kind: 'product',
			label: 'Products',
			help: 'Which product here each legacy product becomes. An unmapped product blocks every order and subscription that names it — historical ones included, because an order cannot be written without a product and a price.'
		},
		{
			kind: 'family',
			label: 'Product families',
			help: 'A legacy group becomes a product family only when every product in it was unique. A group that allowed a customer to hold several at once would have the nightly reconciliation cancel all but one, at Braintree, with no refund.'
		},
		{
			kind: 'tax_rate',
			label: 'Tax rates',
			help: 'The legacy VAT table, with its rates gated on the state that actually charged them. Consumers and businesses each keep their own treatment, as the legacy store kept them.'
		}
	];

	let batches: MigrationBatchSummary[] = [];
	let providers: PaymentProviderConfig[] = [];
	let selected: MigrationBatchSummary | null = null;
	let proposals: Record<string, MigrationProposal[]> = {};
	let report: MigrationDryRunReport | null = null;
	let applied: MigrationApplySummary | null = null;

	/**
	 * Which findings the dry-run table shows, set by clicking the count that names them.
	 *
	 * The counts are the first thing read and the question they raise is "which ones" — so each is the
	 * control that answers it, and clicking it again widens the table back out. Cleared whenever the
	 * report changes, since a filter chosen against the last assessment says nothing about this one.
	 */
	let findingSeverity: '' | MigrationFindingSeverity = '';
	// Keyed on the uuid, not the object: the status poll hands back a new object for the same report,
	// and resetting on that would clear the filter out from under somebody reading the table.
	$: reportUUID = report?.uuid ?? null;
	$: reportUUID, (findingSeverity = '');

	function toggleFindingSeverity(severity: MigrationFindingSeverity): void {
		findingSeverity = findingSeverity === severity ? '' : severity;
	}

	/**
	 * The findings table, paged from the server.
	 *
	 * The report carries a bounded slice, and a batch with thousands of findings had the rest counted
	 * and unreadable — so the table reads the whole set a page at a time, filtered server-side by the
	 * count that was clicked. Back to the first page whenever the filter or the report changes, since
	 * an offset into one list says nothing about another.
	 */
	const FINDINGS_PAGE_SIZE = 50;
	let findingsOffset = 0;
	let findingsPage: MigrationFinding[] = [];
	/**
	 * The offset of the page actually on screen, as distinct from the one asked for: the range is
	 * stated from this, or clicking Next says "101–150" over the rows of 51–100 until the read lands.
	 */
	let findingsPageOffset = 0;
	let findingsTotal = 0;
	let findingsLoading = false;
	let findingsError = '';
	/** Which read is the latest, so a slow page cannot land over the one asked for after it. */
	let findingsRequest = 0;

	/**
	 * Finding types hidden from view, by code — "remove certain advisories from view". One list serves
	 * both gestures: hiding a type adds it, and "only this type" hides every other type on show. Kept
	 * across a change of severity, since hiding a noisy type is a statement about the type, and
	 * cleared with the report, since the next assessment may not have it at all.
	 */
	let hiddenCodes: string[] = [];
	$: reportUUID, (hiddenCodes = []);

	/** The types under the selected count, most numerous first, with how many of each. */
	$: findingTypes = Object.entries(report?.byCode ?? {})
		.filter(([code]) => {
			const severity = report?.codeSeverities?.[code];
			// A report saved before severities were recorded lists every type under every count.
			return findingSeverity === '' || severity === undefined || severity === findingSeverity;
		})
		.sort((left, right) => right[1] - left[1]);

	function toggleHiddenCode(code: string): void {
		hiddenCodes = hiddenCodes.includes(code) ? hiddenCodes.filter(hidden => hidden !== code) : [...hiddenCodes, code];
	}

	function showOnlyCode(code: string): void {
		hiddenCodes = findingTypes.map(([other]) => other).filter(other => other !== code);
	}

	$: reportUUID, findingSeverity, hiddenCodes, (findingsOffset = 0);

	/*
	 * A report saved before every finding was stored has none to page, while its own bounded list is
	 * still there — so the table shows that list, filtered here, and says why it stops.
	 */
	$: isSavedBeforeFindings =
		!findingsLoading && findingsError === '' && findingsTotal === 0 && findingSeverity === '' &&
		hiddenCodes.length === 0 && (report?.findings.length ?? 0) > 0;
	$: shownFindings = isSavedBeforeFindings ? report?.findings ?? [] : findingsPage;
	$: void loadFindings(selected?.uuid ?? null, reportUUID, findingSeverity, hiddenCodes, findingsOffset);

	async function loadFindings(
		batchUUID: string | null,
		forReport: string | null,
		severity: '' | MigrationFindingSeverity,
		excludeCodes: string[],
		offset: number
	): Promise<void> {
		const request = ++findingsRequest;
		if (batchUUID === null || forReport === null) {
			findingsPage = [];
			findingsTotal = 0;
			return;
		}
		findingsLoading = true;
		findingsError = '';
		try {
			const page = await listMigrationDryRunFindings(batchUUID, {
				...(severity === '' ? {} : { severity }),
				...(excludeCodes.length === 0 ? {} : { excludeCodes }),
				limit: FINDINGS_PAGE_SIZE,
				offset
			});
			if (request === findingsRequest) {
				findingsPage = page.items;
				findingsPageOffset = offset;
				findingsTotal = page.total;
			}
		} catch (error) {
			if (request === findingsRequest) {
				findingsError = (error as Error).message;
			}
		} finally {
			if (request === findingsRequest) {
				findingsLoading = false;
			}
		}
	}

	/**
	 * The report's quick fixes, one per **action** with how many findings it resolves.
	 *
	 * Grouped because the findings are not independent: three hundred subscriptions on one unmapped
	 * product are one decision, and a button per row would be three hundred clicks for it — or three
	 * hundred products created, which is worse. Counted by the server over every finding; a report
	 * saved before it did that is grouped from its listed slice instead.
	 */
	$: quickFixes = report?.quickFixes ?? groupQuickFixes(report?.findings ?? []);

	function groupQuickFixes(findings: MigrationDryRunReport['findings']): { fix: MigrationQuickFix; count: number }[] {
		const groups = new Map<string, { fix: MigrationQuickFix; count: number }>();
		for (const finding of findings) {
			if (finding.fix === undefined) {
				continue;
			}
			const group = groups.get(finding.fix.key);
			if (group === undefined) {
				groups.set(finding.fix.key, { fix: finding.fix, count: 1 });
			} else {
				group.count += 1;
			}
		}
		return [...groups.values()].sort((left, right) => right.count - left.count);
	}

	/**
	 * What became of each fix applied against this report, by key.
	 *
	 * A fix changes the store, not the report: its findings stay on screen until the assessment runs
	 * again, so "applied" is said beside them rather than the rows vanishing on a click — which would
	 * read as the problem gone when only its cause is.
	 */
	let fixOutcomes: Record<string, { ok: boolean; message: string }> = {};
	$: reportUUID, (fixOutcomes = {});
	let applyingFix = '';

	/**
	 * Apply one fix, through the same endpoint the screen offers for doing it by hand — so the guard
	 * rails that endpoint enforces (a mapping on an applied batch, a seats figure on a held product)
	 * refuse here exactly as they would there, in the server's own words.
	 */
	async function applyQuickFix(fix: MigrationQuickFix): Promise<void> {
		if (selected === null) {
			return;
		}
		applyingFix = fix.key;
		try {
			switch (fix.kind) {
				case 'acceptMapping':
					await decideMigrationMappings(selected.uuid, [
						{ kind: 'product', sourceKey: fix.legacyProductID, targetID: fix.productID }
					]);
					await refreshPanel('product');
					break;
				case 'createProduct': {
					const [made] = (await createMigrationProducts(selected.uuid, [fix.legacyProductID])).items;
					await refreshPanel('product');
					if (made === undefined || made.outcome === 'refused') {
						throw new Error(made?.reason ?? 'The catalogue refused to create it.');
					}
					break;
				}
				case 'setSeatsPerUnit':
					await updateProduct(fix.productUUID, { seatsPerUnit: fix.seatsPerUnit });
					break;
				case 'addCurrency':
					await createCurrency({
						code: fix.code,
						name: fix.name,
						symbol: fix.symbol,
						minorUnitDigits: fix.minorUnitDigits,
						isEnabled: false
					});
					break;
			}
			fixOutcomes = { ...fixOutcomes, [fix.key]: { ok: true, message: 'Applied. Run the dry run again to clear these findings.' } };
		} catch (error) {
			fixOutcomes = { ...fixOutcomes, [fix.key]: { ok: false, message: (error as Error).message } };
			toastError((error as Error).message);
		} finally {
			applyingFix = '';
		}
	}

	let loading: boolean = true;
	let loadError: string = '';
	let busy: string = '';
	let providerUUID: string = '';
	let file: File | null = null;
	let documentsFile: File | null = null;
	let documentsSummary: MigrationDocumentImportStatus | null = null;
	let documentsTimer: number | null = null;
	const DOCUMENTS_POLL_MS = 2000;

	/**
	 * Whether the operator has said, explicitly, to apply despite the blocking findings.
	 *
	 * Reset whenever a batch is opened or reassessed, because it is an acknowledgement of a
	 * *particular* report — carrying it across a new dry run would be an override of findings
	 * nobody had read.
	 */
	let acknowledgeBlocking: boolean = false;
	/**
	 * The server's sentence naming the applications scoped to what this batch writes, once an Apply
	 * has been refused over them — null until then. Those applications would hear every customer as
	 * they are imported rather than at step 6, so going ahead is a tick of its own.
	 */
	let listeningApplications: string | null = null;
	let acknowledgeListeningApplications: boolean = false;
	/** The same for an undo: set once one is refused because an application has already heard. */
	let informedApplications: string | null = null;
	let acknowledgeApplicationsInformed: boolean = false;

	/** What the last notify sent, and which applications it could not reach. */
	let notified: MigrationNotifySummary | null = null;

	/** What the last undo removed, so the operator can see what it kept as well as what it took. */
	let reverted: MigrationRevertSummary | null = null;

	/** Where the batch's latest assessment has got to, or null before one has been asked for. */
	let dryRunStatus: MigrationDryRunStatus | null = null;
	let dryRunTimer: number | null = null;

	/**
	 * How often the screen asks how the assessment is going.
	 *
	 * Slower than the run's own progress writes, because there is nothing to be gained from asking
	 * more often than the number moves — and this is polled by every admin who leaves the tab open.
	 */
	const DRY_RUN_POLL_MS = 4000;

	/** What each phase is called on screen. The wire values are the row's own vocabulary. */
	const DRY_RUN_PHASES: Record<string, string> = {
		queued: 'Starting',
		fetching: 'Re-reading subscriptions from Braintree',
		assessing: 'Assessing the staged records',
		walking: 'Walking Braintree for subscriptions the export does not carry',
		saving: 'Writing the report',
		complete: 'Finished',
		failed: 'Failed'
	};

	/** What each panel may point a row at, per kind. Empty means "not chosen from a list". */
	let targets: Record<string, MigrationMappingTarget[]> = {};

	/**
	 * The edit in progress for one row, keyed `kind:sourceKey`.
	 *
	 * Held apart from `proposals` rather than bound into it, for the reason the mapping is worth
	 * editing at all: the row carries what the *server* holds, and the difference between the two
	 * is what Save submits. Binding into the row would make every render look decided and leave
	 * nothing able to say which rows an admin actually touched.
	 */
	let drafts: Record<string, Draft> = {};

	/** One row's pending edit. `targetValue` is the wire value; the tax fields build it. */
	interface Draft {
		targetID: number | null;
		targetValue: string | null;
		note: string;
		/** Percent as the admin types it — tax rows only. Kept as text so `19.` survives a keystroke. */
		consumerPercent: string;
		businessPercent: string;
		/** The consumer's treatment, from the legacy `taxStateB2C`. */
		mode: string;
		/** The business's treatment, from the legacy `taxStateB2B` — decided separately, as there. */
		businessMode: string;
	}

	/**
	 * The three treatments a jurisdiction gets, and the only values `tax_rates.mode` and
	 * `tax_rates.businessMode` accept.
	 */
	const TAX_MODES: { value: string; label: string }[] = [
		{ value: 'standard', label: 'Standard — charge the rate' },
		{ value: 'reverse_charge', label: 'Reverse charge' },
		{ value: 'no_tax', label: 'No tax' }
	];

	const draftKey = (kind: string, sourceKey: string): string => `${kind}:${sourceKey}`;

	/**
	 * The draft a row starts from: exactly what the server holds for it.
	 *
	 * A tax row's target is a JSON blob rather than an id, so it is unpacked into the four fields
	 * the form edits and repacked on change. A blob this build cannot read is treated as no answer
	 * rather than as zero — a rate silently read as 0% is a jurisdiction that stops being charged.
	 */
	/**
	 * The colour a batch's status renders in.
	 *
	 * `reverted` and `reverting` fell to the `blue` default, so an import that had been undone looked
	 * exactly like one merely staged — on the screen whose whole job is saying what has happened to
	 * each batch. Amber rather than red: it is a deliberate act that succeeded, not a fault.
	 */
	function statusColor(status: string): 'green' | 'red' | 'yellow' | 'blue' {
		if (status === 'applied') return 'green';
		if (status === 'failed') return 'red';
		if (status === 'reverted' || status === 'reverting' || status === 'partially_applied') {
			return 'yellow';
		}
		return 'blue';
	}

	function draftOf(row: MigrationProposal): Draft {
		let consumerPercent = '';
		let businessPercent = '';
		let mode = 'standard';
		let businessMode = 'standard';

		if (row.kind === 'tax_rate' && row.targetValue !== null) {
			try {
				const parsed = JSON.parse(row.targetValue) as {
					basisPoints?: number;
					businessBasisPoints?: number;
					mode?: string;
					businessMode?: string;
				};
				consumerPercent = majorUnitsFrom(Number(parsed.basisPoints ?? 0), 2);
				businessPercent = majorUnitsFrom(Number(parsed.businessBasisPoints ?? 0), 2);
				mode = typeof parsed.mode === 'string' ? parsed.mode : 'standard';
				// A decision saved before the split carries one mode, which applied to both sides —
				// the server reads it the same way, so the form must not show businesses as standard.
				businessMode = typeof parsed.businessMode === 'string' ? parsed.businessMode : mode;
			} catch {
				// Left blank, which the row renders as unanswered and Save refuses to submit.
			}
		}

		return {
			targetID: row.targetID,
			targetValue: row.targetValue,
			note: row.note ?? '',
			consumerPercent,
			businessPercent,
			mode,
			businessMode
		};
	}

	/** A tax row's four fields back into the wire value, or null when a percent will not parse. */
	function taxTargetValue(draft: Draft): string | null {
		const consumer = minorUnitsFrom(draft.consumerPercent, 2);
		const business = minorUnitsFrom(draft.businessPercent, 2);
		if (consumer === null || business === null) {
			return null;
		}
		return JSON.stringify({
			basisPoints: consumer,
			businessBasisPoints: business,
			mode: draft.mode,
			businessMode: draft.businessMode
		});
	}

	/** Re-packs a tax row's fields into its wire value after any of the four moves. */
	function onTaxEdited(key: string): void {
		const draft = drafts[key];
		if (draft === undefined) {
			return;
		}
		draft.targetValue = taxTargetValue(draft);
		drafts = drafts;
	}

	/** Whether a row's draft says something different from what the server holds. */
	function isDirty(row: MigrationProposal): boolean {
		const draft = drafts[draftKey(row.kind, row.sourceKey)];
		if (draft === undefined) {
			return false;
		}
		return (
			draft.targetID !== row.targetID ||
			draft.targetValue !== row.targetValue ||
			draft.note !== (row.note ?? '')
		);
	}

	/**
	 * What the last "create in catalogue" said about each product it was asked for, by legacy id.
	 *
	 * Kept beside the rows rather than only toasted: a refusal names what to do instead — most often
	 * "this product already exists, map to it" — and a toast that has dismissed itself cannot be read
	 * back while the admin is doing that.
	 */
	let creations: Record<string, MigrationCreatedProduct> = {};

	/**
	 * A legacy product the catalogue could be asked to create: nothing here matched it, and nobody
	 * has decided it should not be imported. A row an admin set to "Do not import" is left out of the
	 * bulk action — creating it anyway would undo a decision somebody made.
	 */
	function isCreatable(row: MigrationProposal): boolean {
		return row.kind === 'product' && row.targetID === null && row.decidedBy !== 'admin';
	}

	$: creatableCount = (proposals['product'] ?? []).filter(row => isCreatable(row)).length;

	/**
	 * Create legacy products in the catalogue and map each onto what was made.
	 *
	 * The server maps them, so the screen re-reads the batch afterwards rather than patching rows: the
	 * new products are also new mapping targets, and the other rows' pickers have to offer them.
	 */
	async function createProducts(sourceKeys: string[]): Promise<void> {
		if (selected === null || sourceKeys.length === 0) {
			return;
		}
		busy = 'create';
		try {
			const { items } = await createMigrationProducts(selected.uuid, sourceKeys);
			const made = items.filter(item => item.outcome !== 'refused').length;
			const refused = items.length - made;
			await refreshPanel('product');
			creations = { ...creations, ...Object.fromEntries(items.map(item => [item.sourceKey, item])) };
			if (refused === 0) {
				toastSuccess(`Created ${made} product${made === 1 ? '' : 's'}, unpublished`);
			} else {
				toastError(`${made} created, ${refused} refused — the reasons are on their rows`);
			}
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	/** A tax row whose percent will not parse: flagged rather than submitted as zero (recipe 8d). */
	function isUnparseable(row: MigrationProposal): boolean {
		const draft = drafts[draftKey(row.kind, row.sourceKey)];
		return row.kind === 'tax_rate' && draft !== undefined && draft.targetValue === null;
	}

	$: dirtyByKind = Object.fromEntries(
		MAPPING_KINDS.map(panel => [
			panel.kind,
			(proposals[panel.kind] ?? []).filter(row => isDirty(row)).length
		])
	) as Record<string, number>;

	$: unparseableByKind = Object.fromEntries(
		MAPPING_KINDS.map(panel => [
			panel.kind,
			(proposals[panel.kind] ?? []).filter(row => isUnparseable(row)).length
		])
	) as Record<string, number>;

	/**
	 * Submit one panel's edits.
	 *
	 * Only the rows that changed, because the endpoint records `decidedBy: 'admin'` on everything it
	 * is sent — submitting the whole panel would mark every untouched row as reviewed, which clears
	 * its blocking state and is exactly the mistake the blocking count exists to catch.
	 */
	async function saveMappings(kind: string): Promise<void> {
		if (selected === null) {
			return;
		}
		const rows = (proposals[kind] ?? []).filter(row => isDirty(row) && !isUnparseable(row));
		if (rows.length === 0) {
			return;
		}
		busy = kind;
		try {
			await decideMigrationMappings(
				selected.uuid,
				rows.map(row => {
					const draft = drafts[draftKey(row.kind, row.sourceKey)];
					const decision: MigrationDecision = {
						kind: row.kind,
						sourceKey: row.sourceKey,
						targetID: draft.targetID,
						targetValue: draft.targetValue,
						note: draft.note.trim() === '' ? null : draft.note.trim()
					};
					return decision;
				})
			);
			await refreshPanel(kind);
			toastSuccess(`Saved ${rows.length} mapping${rows.length === 1 ? '' : 's'}`);
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	/** Which step the operator is on, derived rather than stored (§ the flow's own rule). */
	$: step = selected === null ? 1 : report === null ? 2 : report.isBlocked ? 2 : applied === null ? 3 : 4;

	$: blockingByKind = Object.fromEntries(
		Object.entries(proposals).map(([kind, rows]) => [kind, rows.filter(row => row.isBlocking).length])
	) as Record<string, number>;

	$: undecidedByKind = Object.fromEntries(
		Object.entries(proposals).map(([kind, rows]) => [kind, rows.filter(row => row.decidedBy === 'proposed').length])
	) as Record<string, number>;

	async function load(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			batches = (await listMigrationBatches()).items;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}

		// The provider list failing must not take the screen down with it: an operator reading the
		// records of a batch applied last week does not need a provider picker.
		try {
			// An import lands on a Braintree row and nothing else: every mapping in §7 reads a
			// Braintree plan id, so offering a Stripe provider here would stage a batch that could
			// never be applied.
			const listed = await listPaymentProviders();
			providers = listed.items.filter(provider => provider.type === 'braintree');
			providerUUID = providers[0]?.uuid ?? '';
		} catch {
			providers = [];
		}

		// An upload still being read when the screen was left — or reloaded — picks up where it
		// is rather than vanishing. Swallowed: this is a courtesy, and the panel works without it.
		if (providerUUID !== '') {
			try {
				const latest = await readMigrationDocumentImport(providerUUID);
				if (latest.uuid !== null) {
					documentsSummary = latest;
					watchDocuments();
				}
			} catch {
				// Nothing to resume.
			}
		}
	}

	/*
	 * A handler rather than an inline expression: a TypeScript cast in the markup is not
	 * preprocessed, and `event.currentTarget` is only usefully typed inside the script block.
	 */
	function onFileChosen(event: Event): void {
		file = (event.currentTarget as HTMLInputElement).files?.[0] ?? null;
	}

	function onDocumentsChosen(event: Event): void {
		documentsFile = (event.currentTarget as HTMLInputElement).files?.[0] ?? null;
	}

	/**
	 * Upload the legacy invoice PDFs.
	 *
	 * Takes no batch, because a document belongs to whichever invoice carries its legacy id rather
	 * than to a staging row — so a second upload of the same archive converges instead of
	 * duplicating, and an archive taken before the apply reports everything unmatched rather than
	 * failing in a way that reads as a bad file.
	 */
	async function uploadDocuments(): Promise<void> {
		if (documentsFile === null || providerUUID === '') {
			return;
		}
		busy = 'documents';
		try {
			documentsSummary = await uploadMigrationDocuments(providerUUID, documentsFile);
			watchDocuments();
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	/**
	 * Poll while the archive is being read. The upload answers as soon as the bytes have arrived —
	 * reading them inside the request outlasted the server's request timeout — so this is where
	 * the outcome is learned. Cleared on the way out, like the dry run's timer.
	 */
	function watchDocuments(): void {
		stopWatchingDocuments();
		if (documentsSummary === null || !documentsSummary.isRunning || documentsSummary.isStale) {
			return;
		}
		const watched = providerUUID;
		documentsTimer = window.setInterval(async () => {
			try {
				documentsSummary = await readMigrationDocumentImport(watched);
				if (!documentsSummary.isRunning || documentsSummary.isStale) {
					stopWatchingDocuments();
					if (documentsSummary.error !== null) {
						toastError(documentsSummary.error);
					} else if (documentsSummary.phase === 'complete') {
						toastSuccess(`Stored ${documentsSummary.stored} invoice document(s)`);
					}
				}
			} catch {
				// Swallowed for the dry run's reason: one failed poll is not a failed import.
			}
		}, DOCUMENTS_POLL_MS);
	}

	function stopWatchingDocuments(): void {
		if (documentsTimer !== null) {
			window.clearInterval(documentsTimer);
			documentsTimer = null;
		}
	}

	onDestroy(stopWatchingDocuments);

	async function upload(): Promise<void> {
		if (file === null || providerUUID === '') {
			return;
		}
		busy = 'upload';
		try {
			const staged = await uploadMigrationExport(providerUUID, file);
			toastSuccess(`Staged ${Object.values(staged.counts).reduce((total, count) => total + count, 0)} records`);
			await load();
			await select(staged.uuid);
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	async function select(uuid: string): Promise<void> {
		busy = 'select';
		stopWatchingDryRun();
		report = null;
		reverted = null;
		creations = {};
		notified = null;
		acknowledgeBlocking = false;
		listeningApplications = null;
		acknowledgeListeningApplications = false;
		informedApplications = null;
		acknowledgeApplicationsInformed = false;
		dryRunStatus = null;
		applied = null;
		proposals = {};
		drafts = {};
		targets = {};
		try {
			selected = await getMigrationBatch(uuid);

			/*
			 * Built in locals and published in one go at the end.
			 *
			 * Assigning into `proposals` inside the loop would publish rows before their drafts
			 * existed — and there is an `await` between the two reads, so Svelte really does get a
			 * chance to render in between. Every editor below binds into `drafts[key]`, so that
			 * render is a crash on `undefined`, on the one screen where a crash means an operator
			 * cannot see what a half-mapped batch says.
			 */
			const loadedProposals: Record<string, MigrationProposal[]> = {};
			const loadedTargets: Record<string, MigrationMappingTarget[]> = {};
			const seeded: Record<string, Draft> = {};

			for (const panel of MAPPING_KINDS) {
				loadedProposals[panel.kind] = (await listMigrationProposals(uuid, panel.kind)).items;
				// Re-read on every select rather than once on mount: a product added to the
				// catalogue while a batch sits half-mapped is exactly the product somebody came
				// back to point a row at.
				loadedTargets[panel.kind] = (await listMigrationMappingTargets(panel.kind)).items;
				for (const row of loadedProposals[panel.kind]) {
					seeded[draftKey(row.kind, row.sourceKey)] = draftOf(row);
				}
			}

			// Replaced whole rather than merged: a draft left over from the previously opened batch
			// would be an edit attributed to a row somebody never looked at.
			drafts = seeded;
			targets = loadedTargets;
			proposals = loadedProposals;

			/*
			 * An assessment started before this page was opened — or before it was reloaded — is
			 * still running on the server, and the screen has to find it rather than offering a
			 * button that would be refused for starting a second.
			 */
			dryRunStatus = await readMigrationDryRun(uuid);
			report = dryRunStatus.report;
			watchDryRun();
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	/** The migration search: what was typed, and what it found — null until something was searched. */
	let lookupTerm: string = '';
	let lookupResults: MigrationLookupResult[] | null = null;
	let lookupBusy: boolean = false;

	/** The words for each import outcome, and the badge colour it reads in. */
	const OUTCOME_COLORS: Record<string, 'green' | 'yellow' | 'red' | 'dark'> = {
		created: 'green',
		updated: 'green',
		skipped: 'yellow',
		deferred: 'yellow',
		unlinked: 'yellow',
		failed: 'red'
	};

	/** Which of the record's ids the search matched, said the way support would say it. */
	const MATCHED_ON: Record<MigrationLookupResult['matchedOn'], string> = {
		legacyID: 'its legacy id',
		braintreeTransactionID: 'its Braintree transaction',
		braintreeSubscriptionID: 'its Braintree subscription',
		firstTransaction: 'its first transaction (the licence id)'
	};

	/**
	 * Find a legacy record by any id it is known by. Its own busy flag rather than `busy`: a lookup
	 * reads and writes nothing, so it must not lock the mapping screen, nor wait on it.
	 */
	async function lookup(): Promise<void> {
		if (lookupTerm.trim() === '') {
			return;
		}
		lookupBusy = true;
		try {
			lookupResults = (await lookupMigrationRecord(lookupTerm.trim())).items;
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			lookupBusy = false;
		}
	}

	/**
	 * Re-read one mapping panel after a write to it, in place.
	 *
	 * Not `select()`, which is what this used to call: that empties every panel, the dry-run report
	 * and every draft before loading the batch again, so the page collapsed to its header and rebuilt
	 * beneath the admin — scrolled away from the row they had just acted on, with every unsaved edit
	 * in the other panels thrown away. Only this panel's rows and targets can have moved (a created
	 * product is a new target for every other row in it), plus the batch's own status.
	 *
	 * A row's unsaved edit survives unless the server moved that row: its edit was made against what
	 * it showed before, and an edit to a row that has since been decided would save over the decision.
	 * Drafts are published before the rows, for the reason `select` gives — every editor binds into
	 * `drafts[key]`, and a row rendered without one is a crash.
	 */
	async function refreshPanel(kind: string): Promise<void> {
		if (selected === null) {
			return;
		}
		const uuid = selected.uuid;
		const [batch, rows, kindTargets] = await Promise.all([
			getMigrationBatch(uuid),
			listMigrationProposals(uuid, kind),
			listMigrationMappingTargets(kind)
		]);

		const previous = new Map((proposals[kind] ?? []).map(row => [row.sourceKey, row]));
		const nextDrafts = { ...drafts };
		for (const row of rows.items) {
			const before = previous.get(row.sourceKey);
			const serverMoved =
				before === undefined ||
				before.targetID !== row.targetID ||
				before.targetValue !== row.targetValue ||
				(before.note ?? '') !== (row.note ?? '');
			if (serverMoved || !isDirty(before)) {
				nextDrafts[draftKey(row.kind, row.sourceKey)] = draftOf(row);
			}
		}

		drafts = nextDrafts;
		targets = { ...targets, [kind]: kindTargets.items };
		proposals = { ...proposals, [kind]: rows.items };
		selected = batch;
	}

	/** Accept every standing proposal in one panel, audited as one decision naming each. */
	async function acceptAll(kind: string): Promise<void> {
		if (selected === null) {
			return;
		}
		const rows = (proposals[kind] ?? []).filter(row => row.decidedBy === 'proposed');
		if (rows.length === 0) {
			return;
		}
		busy = kind;
		try {
			await decideMigrationMappings(
				selected.uuid,
				rows.map(row => ({
					kind: row.kind,
					sourceKey: row.sourceKey,
					targetID: row.targetID,
					targetValue: row.targetValue
				}))
			);
			await refreshPanel(kind);
			toastSuccess(`Accepted ${rows.length} proposal${rows.length === 1 ? '' : 's'}`);
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	/**
	 * Start the assessment and watch it.
	 *
	 * `busy` is deliberately cleared as soon as the run has *started*: the work outlives the
	 * request, so holding the screen would lock every other control on it for a quarter of an hour
	 * over a request that finished in a moment.
	 */
	/**
	 * Seed the consuming applications with what this batch imported.
	 *
	 * A step of its own rather than part of the apply, because the apply delivers nothing on purpose:
	 * one row per subscribed application per change is a flood across a customer base.
	 */
	async function notify(): Promise<void> {
		if (selected === null) {
			return;
		}
		busy = 'notify';
		try {
			notified = await notifyMigrationApplications(selected.uuid);
			toastSuccess(`Queued ${notified.deliveries} delivery(s) for ${notified.customers} customer(s)`);
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	/**
	 * Undo an applied batch.
	 *
	 * Reloads afterwards rather than patching the screen: the batch's status, its counts and what the
	 * mapping panels say are all different once the rows are gone, and a half-refreshed screen after
	 * an operation this size is worse than a pause.
	 */
	async function revert(): Promise<void> {
		if (selected === null) {
			return;
		}
		busy = 'revert';
		try {
			reverted = await revertMigrationBatch(selected.uuid, acknowledgeApplicationsInformed);
			informedApplications = null;
			toastSuccess(
				`Removed ${reverted.orders + reverted.subscriptions} purchase(s) and ${reverted.invoices} invoice(s)`
			);
			await load();
			await select(selected.uuid);
		} catch (error) {
			// A refusal here is a question rather than a fault — money or paperwork has moved since the
			// import — and the server's own sentence says which, so it is rendered rather than replaced.
			toastError((error as Error).message);
			if (conditionOf(error) === 'migration.revertApplicationsInformed') {
				informedApplications = (error as Error).message;
			}
		} finally {
			busy = '';
		}
	}

	async function dryRun(): Promise<void> {
		if (selected === null) {
			return;
		}
		busy = 'dry-run';
		try {
			acknowledgeBlocking = false;
			listeningApplications = null;
			acknowledgeListeningApplications = false;
			dryRunStatus = await runMigrationDryRun(selected.uuid);
			report = dryRunStatus.report;
			watchDryRun();
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	/**
	 * Poll while an assessment is in flight.
	 *
	 * Cleared on the way out as well as on completion: a timer left running against a screen that
	 * has been navigated away from is a request every few seconds for the life of the tab, and it
	 * would go on writing into component state nothing is rendering.
	 */
	function watchDryRun(): void {
		stopWatchingDryRun();
		if (dryRunStatus === null || !dryRunStatus.isRunning) {
			return;
		}
		dryRunTimer = window.setInterval(async () => {
			if (selected === null) {
				stopWatchingDryRun();
				return;
			}
			try {
				dryRunStatus = await readMigrationDryRun(selected.uuid);
				if (dryRunStatus.report !== null) {
					report = dryRunStatus.report;
				}
				if (!dryRunStatus.isRunning || dryRunStatus.isStale) {
					stopWatchingDryRun();
					if (dryRunStatus.error !== null) {
						toastError(dryRunStatus.error);
					} else if (dryRunStatus.report !== null) {
						toastSuccess('The assessment finished');
					}
				}
			} catch {
				// Swallowed: a poll that fails is a poll, and the next one is a few seconds away.
				// Failing the screen over it would replace a progress bar with an error state on a
				// run that is still perfectly alive.
			}
		}, DRY_RUN_POLL_MS);
	}

	function stopWatchingDryRun(): void {
		if (dryRunTimer !== null) {
			window.clearInterval(dryRunTimer);
			dryRunTimer = null;
		}
	}

	onDestroy(stopWatchingDryRun);

	async function apply(): Promise<void> {
		if (selected === null) {
			return;
		}
		busy = 'apply';
		try {
			applied = await applyMigrationBatch(
				selected.uuid,
				acknowledgeBlocking,
				acknowledgeListeningApplications
			);
			listeningApplications = null;
			await load();
			toastSuccess(`Imported ${applied.customers} customer${applied.customers === 1 ? '' : 's'}`);
		} catch (error) {
			// A stale dry run and a blocked one are questions rather than faults, and the server's
			// own sentence says which — so it is rendered rather than replaced.
			toastError((error as Error).message);
			if (conditionOf(error) === 'migration.dryRunStale') {
				report = null;
			}
			if (conditionOf(error) === 'migration.applicationsListening') {
				listeningApplications = (error as Error).message;
			}
		} finally {
			busy = '';
		}
	}

	async function discard(uuid: string): Promise<void> {
		busy = 'discard';
		try {
			await discardMigrationBatch(uuid);
			selected = null;
			report = null;
			await load();
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	onMount(load);
</script>

<PageHeader
	title="Migration"
	description="Bring the legacy store's customers, subscriptions and purchase history here."
/>

<!--
	One wrapper for the whole sequence, as every other admin page of panels does (Operations, Sales,
	Reports, Invoices, Analytics). `space-y-6` is a *sibling* rule — `> :not([hidden]) ~ :not([hidden])`
	— so it reaches the panels inside the `{#if}` and the `{#each}` below just the same: those blocks
	render no element of their own, and their contents are direct children of this div.

	`min-w-0` for the reason the table rule gives: several of these panels hold wide tables that
	scroll inside their own `overflow-x-auto`, and without it the widest one sets this container's
	floor and the *page* grows sideways instead.
-->
<div class="space-y-6 min-w-0">
	<!--
		Above the sequence rather than inside it: the question it answers — "what happened to this
		transaction" — arrives with an id off a receipt or a licence and no batch attached, and it keeps
		being asked long after the import is done.
	-->
	<Panel title="Find a legacy record" faIcon="fa-magnifying-glass">
		<p class="text-default text-sm">
			Search every batch by a legacy transaction or subscription id, a Braintree transaction or
			subscription id, or the transaction id a licence was issued for.
		</p>
		<form class="mt-3 flex flex-wrap items-center gap-2" on:submit|preventDefault={lookup}>
			<input
				class="min-w-0 flex-1 rounded-lg bg-depth border-widget-color text-sm"
				type="text"
				placeholder="Transaction or subscription id"
				aria-label="Legacy id to find"
				bind:value={lookupTerm}
			/>
			<Button type="submit" size="sm" disabled={lookupBusy || lookupTerm.trim() === ''}>
				{lookupBusy ? 'Searching…' : 'Search'}
			</Button>
		</form>

		{#if lookupResults !== null}
			{#if lookupResults.length === 0}
				<EmptyState
					variant="empty"
					title="Nothing found"
					description="No staged transaction or subscription carries that id, in any batch."
				/>
			{:else}
				<div class="min-w-0 overflow-x-auto mt-3">
					<Table class="dataTable">
						<TableHead>
							<TableHeadCell>Legacy record</TableHeadCell>
							<TableHeadCell>In the legacy store</TableHeadCell>
							<TableHeadCell>Import</TableHeadCell>
							<TableHeadCell>Here</TableHeadCell>
						</TableHead>
						<TableBody>
							{#each lookupResults as result (`${result.batchUUID}:${result.kind}:${result.sourceID}`)}
								<TableBodyRow>
									<TableBodyCell>
										<span class="capitalize">{result.kind}</span>
										<span class="block font-mono text-xs">{result.sourceID}</span>
										<span class="block text-default text-xs">Found by {MATCHED_ON[result.matchedOn]}</span>
									</TableBodyCell>
									<TableBodyCell>
										<span class="block">{result.legacy.product ?? 'Unnamed product'}</span>
										<span class="block text-default text-xs">
											{[result.legacy.status, result.legacy.amount, result.legacy.date?.slice(0, 10), result.legacy.invoiceNo === null ? null : `invoice ${result.legacy.invoiceNo}`]
												.filter(part => part !== null && part !== undefined && part !== '')
												.join(' · ')}
										</span>
										{#if result.legacy.braintreeTransactionID !== null || result.legacy.braintreeSubscriptionID !== null}
											<span class="block text-default text-xs font-mono">
												{[result.legacy.braintreeTransactionID, result.legacy.braintreeSubscriptionID].filter(part => part !== null).join(' · ')}
											</span>
										{/if}
									</TableBodyCell>
									<TableBodyCell>
										{#if result.outcome === null}
											<!-- Staged and never applied: the one state with no record, and not a failure. -->
											<span class="text-default text-xs">Not imported yet</span>
										{:else}
											<Badge color={OUTCOME_COLORS[result.outcome.outcome] ?? 'dark'}>{result.outcome.outcome}</Badge>
											{#if result.outcome.reason !== null}
												<span class="block text-default text-xs mt-1">{result.outcome.reason}</span>
											{/if}
										{/if}
										<span class="block text-default text-xs mt-1">Batch {result.batchStatus.replace('_', ' ')}</span>
									</TableBodyCell>
									<TableBodyCell>
										<!-- Each link only where the import wrote the thing it opens: a dead link on
										     a support screen reads as a record that went missing. -->
										{#if result.links.subscriptionUUID !== null}
											<RoutedLink class="block text-primary-600 dark:text-primary-400 hover:underline text-sm" to={`/Transactions/Subscription/${result.links.subscriptionUUID}`}>
												Subscription
											</RoutedLink>
										{/if}
										{#if result.links.orderUUID !== null}
											<RoutedLink class="block text-primary-600 dark:text-primary-400 hover:underline text-sm" to={`/Transactions/Order/${result.links.orderUUID}`}>
												Order
											</RoutedLink>
										{/if}
										{#if result.links.customerUserUUID !== null}
											<RoutedLink class="block text-primary-600 dark:text-primary-400 hover:underline text-sm" to={`/Customers/${result.links.customerUserUUID}`}>
												Customer
											</RoutedLink>
										{/if}
										{#if result.links.subscriptionUUID === null && result.links.orderUUID === null && result.links.customerUserUUID === null}
											<span class="text-default text-xs">Nothing written</span>
										{/if}
									</TableBodyCell>
								</TableBodyRow>
							{/each}
						</TableBody>
					</Table>
				</div>
				{#if lookupResults.length >= 50}
					<p class="text-default text-xs mt-2">Showing the first 50 matches; a more specific id narrows it.</p>
				{/if}
			{/if}
		{/if}
	</Panel>

	<Panel title="1 · Upload an export" faIcon="fa-file-import">
		{#if providers.length === 0}
			<EmptyState
				variant="empty"
				title="No Braintree provider"
				description="An import lands on one Braintree provider row, and there is none configured. The legacy store's merchant account has to exist here before its customers can."
			/>
		{:else}
			<div class="grid gap-3 sm:grid-cols-2">
				<label class="text-sm">
					<span class="card-label">Provider</span>
					<select class="w-full rounded-lg bg-depth border-widget-color text-sm" bind:value={providerUUID}>
						{#each providers as provider (provider.uuid)}
							<option value={provider.uuid}>{provider.type} · {provider.mode}</option>
						{/each}
					</select>
				</label>
				<label class="text-sm">
					<span class="card-label">Export file</span>
					<input
						class="w-full rounded-lg bg-depth border-widget-color text-sm"
						type="file"
						accept=".gz,.ndjson,.jsonl,application/gzip,application/x-ndjson"
						on:change={onFileChosen}
					/>
				</label>
			</div>
			<p class="text-default text-xs mt-2">
				The file streams straight into staging — nothing here holds it in memory, so an export of
				any size is fine. A truncated download is refused rather than partly imported.
			</p>
			<Button class="mt-3" disabled={file === null || busy !== ''} on:click={upload}>
				{busy === 'upload' ? 'Uploading…' : 'Upload'}
			</Button>
		{/if}
	</Panel>

	<Panel title="Batches" faIcon="fa-layer-group">
		{#if loading}
			<EmptyState variant="loading" title="Loading" description="Reading the staged batches." />
		{:else if loadError !== ''}
			<EmptyState variant="error" title="Could not load" description={loadError} />
		{:else if batches.length === 0}
			<EmptyState variant="empty" title="Nothing staged" description="Upload a legacy export to begin." />
		{:else}
			<div class="min-w-0 overflow-x-auto">
				<Table class="dataTable">
					<TableHead>
						<TableHeadCell>Uploaded</TableHeadCell>
						<TableHeadCell>Merchant</TableHeadCell>
						<TableHeadCell>Status</TableHeadCell>
						<TableHeadCell></TableHeadCell>
					</TableHead>
					<TableBody>
						{#each batches as batch (batch.uuid)}
							<TableBodyRow>
								<TableBodyCell>{new Date(batch.creationDate).toLocaleString()}</TableBodyCell>
								<TableBodyCell>{batch.sourceMerchantID}</TableBodyCell>
								<TableBodyCell>
									<Badge color={batch.status === 'applied' ? 'green' : batch.status === 'failed' ? 'red' : 'blue'}>
										{batch.status}
									</Badge>
								</TableBodyCell>
								<TableBodyCell>
									<Button size="xs" disabled={busy !== ''} on:click={() => select(batch.uuid)}>Open</Button>
									{#if batch.appliedAt === null}
										<Button size="xs" color="alternative" disabled={busy !== ''} on:click={() => discard(batch.uuid)}>
											Discard
										</Button>
									{/if}
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			</div>
		{/if}
	</Panel>

	{#if selected !== null}
		{#each MAPPING_KINDS as panel (panel.kind)}
			<Panel title={`2 · ${panel.label}`} faIcon="fa-diagram-project">
				<p class="text-default text-sm">{panel.help}</p>
				<p class="text-sm mt-2">
					{#if (blockingByKind[panel.kind] ?? 0) > 0}
						<Badge color="red">{blockingByKind[panel.kind]} blocking</Badge>
					{/if}
					<span class="text-default">
						{undecidedByKind[panel.kind] ?? 0} of {(proposals[panel.kind] ?? []).length} still on the
						importer's own proposal
					</span>
				</p>

				{#if panel.kind === 'product' && typeof selected.manifest?.planFetchError === 'string' && selected.manifest.planFetchError !== ''}
					<!-- Said here rather than only at the refusal: every subscription product in this
					     export will be refused creation for the same reason, and the fix is upstream. -->
					<p class="text-red-500 text-sm mt-2">
						The legacy store could not read its Braintree plans when this export was taken
						({selected.manifest.planFetchError}). Subscription products cannot be created from it — take
						the export again. Mapping onto existing products still works.
					</p>
				{/if}

				{#if (proposals[panel.kind] ?? []).length > 0}
					<div class="min-w-0 overflow-x-auto mt-3">
						<Table class="dataTable">
							<TableHead>
								<TableHeadCell>Legacy</TableHeadCell>
								<TableHeadCell>Maps to</TableHeadCell>
								<TableHeadCell>Evidence</TableHeadCell>
								<TableHeadCell>Note</TableHeadCell>
								<TableHeadCell>State</TableHeadCell>
							</TableHead>
							<TableBody>
								{#each proposals[panel.kind] as row (row.sourceKey)}
									<TableBodyRow>
										<TableBodyCell>{row.label}</TableBodyCell>
										<TableBodyCell>
											{#if row.kind === 'tax_rate'}
												<!-- Four figures rather than a choice from a list, so the fields are
												     the editor: a treatment and a rate per audience, as the legacy
												     store kept them. Percent in, basis points out (recipe 8d): the
												     admin types 19, the column stores 1900. -->
												<div class="grid gap-2 sm:grid-cols-2 min-w-[22rem]">
													<label class="text-xs">
														<span class="card-label">Consumer treatment</span>
														<select
															class="w-full rounded-lg bg-depth border-widget-color text-sm"
															bind:value={drafts[draftKey(row.kind, row.sourceKey)].mode}
															on:change={() => onTaxEdited(draftKey(row.kind, row.sourceKey))}
														>
															{#each TAX_MODES as mode (mode.value)}
																<option value={mode.value}>{mode.label}</option>
															{/each}
														</select>
													</label>
													<label class="text-xs">
														<span class="card-label">Consumer %</span>
														<input
															class="w-full rounded-lg bg-depth border-widget-color text-sm tabular-nums"
															class:border-red-500={isUnparseable(row)}
															type="text"
															inputmode="decimal"
															bind:value={drafts[draftKey(row.kind, row.sourceKey)].consumerPercent}
															on:input={() => onTaxEdited(draftKey(row.kind, row.sourceKey))}
														/>
													</label>
													<label class="text-xs">
														<span class="card-label">Business treatment</span>
														<select
															class="w-full rounded-lg bg-depth border-widget-color text-sm"
															bind:value={drafts[draftKey(row.kind, row.sourceKey)].businessMode}
															on:change={() => onTaxEdited(draftKey(row.kind, row.sourceKey))}
														>
															{#each TAX_MODES as mode (mode.value)}
																<option value={mode.value}>{mode.label}</option>
															{/each}
														</select>
													</label>
													<label class="text-xs">
														<span class="card-label">Business %</span>
														<input
															class="w-full rounded-lg bg-depth border-widget-color text-sm tabular-nums"
															class:border-red-500={isUnparseable(row)}
															type="text"
															inputmode="decimal"
															bind:value={drafts[draftKey(row.kind, row.sourceKey)].businessPercent}
															on:input={() => onTaxEdited(draftKey(row.kind, row.sourceKey))}
														/>
													</label>
												</div>
												{#if isUnparseable(row)}
													<p class="text-red-500 text-xs mt-1">
														That is not a percentage. It is left unsaved rather than read as 0%,
														which would stop this jurisdiction being charged.
													</p>
												{/if}
											{:else if row.kind === 'product'}
												<!-- The id comes from the server's own list, so the screen can only
												     ever submit a product this store actually holds. -->
												<select
													class="w-full min-w-[16rem] rounded-lg bg-depth border-widget-color text-sm"
													bind:value={drafts[draftKey(row.kind, row.sourceKey)].targetID}
												>
													<option value={null}>— Do not import —</option>
													{#each targets[row.kind] ?? [] as target (target.id)}
														<option value={target.id}>
															{target.label}{target.hint === null ? '' : ` · ${target.hint}`}
														</option>
													{/each}
												</select>
												<!-- The other answer to "which product is this": one that does not exist
												     yet. Offered only while nothing is mapped, since creating a second
												     product beside the one a row already points at is never the intent. -->
												{#if row.targetID === null}
													<Button
														size="xs"
														color="alternative"
														class="mt-2"
														disabled={busy !== ''}
														on:click={() => createProducts([row.sourceKey])}
													>
														{busy === 'create' ? 'Creating…' : 'Create in catalogue'}
													</Button>
												{/if}
												{#if creations[row.sourceKey]?.outcome === 'refused'}
													<p class="text-red-500 text-xs mt-1">{creations[row.sourceKey].reason}</p>
												{:else if creations[row.sourceKey] !== undefined}
													<p class="text-default text-xs mt-1">
														{creations[row.sourceKey].outcome === 'created' ? 'Created' : 'Already created'}
														as “{creations[row.sourceKey].slug}”, unpublished.
													</p>
												{/if}
												{#each creations[row.sourceKey]?.warnings ?? [] as warning}
													<p class="text-amber-600 dark:text-amber-400 text-xs mt-1">{warning}</p>
												{/each}
											{:else}
												<select
													class="w-full min-w-[16rem] rounded-lg bg-depth border-widget-color text-sm"
													bind:value={drafts[draftKey(row.kind, row.sourceKey)].targetValue}
												>
													{#each targets[row.kind] ?? [] as target (target.value)}
														<option value={target.value}>{target.label}</option>
													{/each}
												</select>
												{#each (targets[row.kind] ?? []).filter(target => target.value === drafts[draftKey(row.kind, row.sourceKey)].targetValue) as chosen (chosen.value)}
													{#if chosen.hint !== null}
														<p class="text-default text-xs mt-1">{chosen.hint}</p>
													{/if}
												{/each}
											{/if}
										</TableBodyCell>
										<TableBodyCell>
											<!-- A SKU match and a name match are not the same quality of claim, and
											     mapping a live subscription onto the wrong product prices somebody at
											     another plan's amount with nothing downstream flagging it. -->
											<Badge color={row.evidence === 'sku' ? 'green' : row.evidence === 'none' ? 'red' : 'yellow'}>
												{row.evidence}
											</Badge>
										</TableBodyCell>
										<TableBodyCell>
											<!-- Why a row was pointed somewhere the proposal did not point it. The
											     endpoint audits it with the decision, so an unexplained override is a
											     choice rather than the only option. -->
											<input
												class="w-full min-w-[10rem] rounded-lg bg-depth border-widget-color text-sm"
												type="text"
												placeholder="Why"
												bind:value={drafts[draftKey(row.kind, row.sourceKey)].note}
											/>
										</TableBodyCell>
										<TableBodyCell>
											{#if isDirty(row)}
												<Badge color="yellow">unsaved</Badge>
											{:else if row.isBlocking}
												<span class="text-red-500 text-xs">{row.blockingReason}</span>
											{:else if row.decidedBy === 'admin'}
												<Badge color="green">decided</Badge>
											{:else}
												<span class="text-default text-xs">proposed</span>
											{/if}
										</TableBodyCell>
									</TableBodyRow>
								{/each}
							</TableBody>
						</Table>
					</div>
					<div class="mt-3 flex flex-wrap items-center gap-2">
						<Button
							size="sm"
							disabled={busy !== '' || (dirtyByKind[panel.kind] ?? 0) === 0}
							on:click={() => saveMappings(panel.kind)}
						>
							{busy === panel.kind ? 'Saving…' : `Save ${dirtyByKind[panel.kind] ?? 0} change(s)`}
						</Button>
						<Button
							size="sm"
							color="alternative"
							disabled={busy !== '' || (undecidedByKind[panel.kind] ?? 0) === 0}
							on:click={() => acceptAll(panel.kind)}
						>
							Accept all {undecidedByKind[panel.kind] ?? 0} proposals
						</Button>
						{#if panel.kind === 'product' && creatableCount > 0}
							<!-- Everything nothing here matched, in one go. Unpublished, so it sells
							     nothing until somebody has read each product and switched it on. -->
							<Button
								size="sm"
								color="alternative"
								disabled={busy !== ''}
								on:click={() =>
									createProducts(
										(proposals['product'] ?? []).filter(row => isCreatable(row)).map(row => row.sourceKey)
									)}
							>
								{busy === 'create' ? 'Creating…' : `Create ${creatableCount} unmatched in the catalogue`}
							</Button>
						{/if}
						{#if (unparseableByKind[panel.kind] ?? 0) > 0}
							<!-- Stated rather than left to the row highlight: Save reports how many rows it
							     will write, and a row it is holding back has to be findable. -->
							<span class="text-red-500 text-xs">
								{unparseableByKind[panel.kind]} row(s) carry a percentage that will not parse and
								are held back.
							</span>
						{/if}
					</div>
				{:else}
					<EmptyState variant="empty" title="Nothing to map" description="This batch carries none of these." />
				{/if}
			</Panel>
		{/each}

		<Panel title="3 · Dry run" faIcon="fa-flask">
			<p class="text-default text-sm">
				Reads every legacy subscription back from Braintree and reports what applying this batch
				would do. It writes nothing.
			</p>
			<p class="text-default text-xs mt-1">
				It re-fetches every legacy subscription from Braintree at the configured budget, so on a
				real store it runs for many minutes. It keeps running if you close this page — come back
				and it will still be here.
			</p>
			<Button
				class="mt-3"
				disabled={busy !== '' || (dryRunStatus?.isRunning === true && dryRunStatus?.isStale !== true)}
				on:click={dryRun}
			>
				{#if busy === 'dry-run'}
					Starting…
				{:else if dryRunStatus?.isRunning === true && dryRunStatus?.isStale !== true}
					Running…
				{:else if dryRunStatus?.isStale === true}
					Start again
				{:else}
					Run
				{/if}
			</Button>

			{#if dryRunStatus !== null && dryRunStatus.isRunning}
				<div class="mt-3 text-sm">
					{#if dryRunStatus.isStale}
						<!-- Running by the row, silent for minutes: the process holding it went away. Said
						     rather than left spinning, because it will never finish on its own. -->
						<p class="text-red-500">
							This assessment stopped reporting and will not finish — the process running it went
							away. Start it again.
						</p>
					{:else}
						<p>
							{DRY_RUN_PHASES[dryRunStatus.phase ?? ''] ?? dryRunStatus.phase}
							{#if dryRunStatus.total > 0}
								— {dryRunStatus.done} of {dryRunStatus.total}
							{:else if dryRunStatus.done > 0}
								— {dryRunStatus.done} so far
							{/if}
						</p>
						{#if dryRunStatus.total > 0}
							<div class="mt-2 h-2 w-full rounded-full bg-depth overflow-hidden">
								<div
									class="h-full bg-primary-600 transition-all"
									style={`width: ${Math.min(100, Math.round((dryRunStatus.done / dryRunStatus.total) * 100))}%`}
								></div>
							</div>
						{/if}
					{/if}
				</div>
			{:else if dryRunStatus?.error !== null && dryRunStatus?.error !== undefined}
				<p class="text-red-500 text-sm mt-3">The last assessment failed: {dryRunStatus.error}</p>
			{/if}

			{#if report !== null}
				<div class="mt-4">
					<!-- Real buttons around the badges, so they are reachable by keyboard and announce their
					     state; `aria-pressed` is what says which one is narrowing the table. -->
					{#if report.isBlocked}
						<button
							type="button"
							class="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
							class:ring-2={findingSeverity === 'blocking'}
							class:ring-red-500={findingSeverity === 'blocking'}
							aria-pressed={findingSeverity === 'blocking'}
							title={findingSeverity === 'blocking' ? 'Show every finding' : 'Show only the blocking findings'}
							on:click={() => toggleFindingSeverity('blocking')}
						>
							<Badge color="red">{report.blocking} blocking</Badge>
						</button>
					{:else}
						<Badge color="green">Nothing blocking</Badge>
					{/if}
					{#if report.advisory > 0}
						<button
							type="button"
							class="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
							class:ring-2={findingSeverity === 'advisory'}
							class:ring-yellow-500={findingSeverity === 'advisory'}
							aria-pressed={findingSeverity === 'advisory'}
							title={findingSeverity === 'advisory' ? 'Show every finding' : 'Show only the findings to acknowledge'}
							on:click={() => toggleFindingSeverity('advisory')}
						>
							<Badge color="yellow">{report.advisory} to acknowledge</Badge>
						</button>
					{:else}
						<Badge color="yellow">0 to acknowledge</Badge>
					{/if}
					<!-- What the importer does by design. Grey and last: nothing here asks for a decision,
					     and a note in the "to acknowledge" column is what buried the ones that did. -->
					{#if (report.notes ?? 0) > 0}
						<button
							type="button"
							class="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
							class:ring-2={findingSeverity === 'note'}
							class:ring-gray-400={findingSeverity === 'note'}
							aria-pressed={findingSeverity === 'note'}
							title={findingSeverity === 'note' ? 'Show every finding' : 'Show only the notes'}
							on:click={() => toggleFindingSeverity('note')}
						>
							<Badge color="dark">{report.notes} note{report.notes === 1 ? '' : 's'}</Badge>
						</button>
					{/if}

					{#if report.providerActiveNotInExport.length > 0}
						<p class="text-red-500 text-sm mt-2">
							Braintree is billing {report.providerActiveNotInExport.length} subscription(s) this
							export does not carry. Importing without them leaves those customers charged every
							month while every application denies them access.
						</p>
					{/if}

					{#if Object.keys(report.durationDistribution).length > 0}
						<p class="text-default text-xs mt-2">
							Legacy product durations: {Object.entries(report.durationDistribution)
								.map(([value, count]) => `${value} (×${count})`)
								.join(', ')} — a column holding both 12 and 365 means the unit is ambiguous.
						</p>
					{/if}

					{#if quickFixes.length > 0}
						<!--
							One row per action, not per finding: three hundred subscriptions on one unmapped
							product are one decision. Only what the store can do safely by itself is here —
							dunning, a period Braintree says has ended, an owner nobody can resolve are
							questions for a person, and get no button.
						-->
						<section class="mt-4" aria-labelledby="quick-fixes-heading">
							<h3 id="quick-fixes-heading" class="card-label">Quick fixes</h3>
							<ul class="mt-2 space-y-2">
								{#each quickFixes as { fix, count } (fix.key)}
									<li class="flex flex-wrap items-center gap-2 text-sm">
										<Button
											size="xs"
											color={fixOutcomes[fix.key]?.ok === true ? 'alternative' : 'primary'}
											disabled={busy !== '' || applyingFix !== '' || fixOutcomes[fix.key]?.ok === true}
											on:click={() => applyQuickFix(fix)}
										>
											{#if applyingFix === fix.key}
												Applying…
											{:else if fixOutcomes[fix.key]?.ok === true}
												<i class="fa-solid fa-check mr-1" />Applied
											{:else}
												Apply
											{/if}
										</Button>
										<span>{fix.label}</span>
										<span class="text-default text-xs">
											— resolves {count} finding{count === 1 ? '' : 's'}
										</span>
										{#if fixOutcomes[fix.key] !== undefined}
											<p
												class="basis-full text-xs {fixOutcomes[fix.key].ok ? 'text-green-600 dark:text-green-400' : 'text-red-500'}"
												aria-live="polite"
											>
												{fixOutcomes[fix.key].message}
											</p>
										{/if}
									</li>
								{/each}
							</ul>
							{#if Object.values(fixOutcomes).some(outcome => outcome.ok)}
								<!-- A fix changes the store, not this report, so the next step is said and offered. -->
								<Button
									size="xs"
									color="alternative"
									class="mt-3"
									disabled={busy !== '' || (dryRunStatus?.isRunning === true && dryRunStatus?.isStale !== true)}
									on:click={dryRun}
								>
									<i class="fa-solid fa-rotate mr-1" />Run the dry run again
								</Button>
							{/if}
						</section>
					{/if}

					{#if report.blocking + report.advisory + (report.notes ?? 0) > 0}
						{#if findingTypes.length > 1}
							<!--
								By type, under whichever count is selected. Clicking a type hides it or brings it
								back; "only" hides every other one — so one noisy type can be taken out of view,
								or one looked at on its own, across every page rather than the one on screen.
							-->
							<div class="mt-3 flex flex-wrap items-center gap-1.5 text-xs" role="group" aria-label="Finding types">
								<span class="card-label mr-1">By type</span>
								{#each findingTypes as [code, count] (code)}
									<span class="inline-flex items-center rounded-full border border-widget-color overflow-hidden">
										<button
											type="button"
											class="px-2 py-0.5 font-mono"
											class:line-through={hiddenCodes.includes(code)}
											class:opacity-50={hiddenCodes.includes(code)}
											aria-pressed={!hiddenCodes.includes(code)}
											title={hiddenCodes.includes(code) ? 'Show this type again' : 'Hide this type'}
											on:click={() => toggleHiddenCode(code)}
										>
											{code} <span class="tabular-nums">({count.toLocaleString()})</span>
										</button>
										<button
											type="button"
											class="px-1.5 py-0.5 border-l border-widget-color text-primary-600 dark:text-primary-400"
											title="Show only this type"
											aria-label={`Show only ${code}`}
											on:click={() => showOnlyCode(code)}
										>
											only
										</button>
									</span>
								{/each}
								{#if hiddenCodes.length > 0}
									<button
										type="button"
										class="underline text-primary-600 dark:text-primary-400 ml-1"
										on:click={() => (hiddenCodes = [])}
									>
										Show all types ({hiddenCodes.length} hidden)
									</button>
								{/if}
							</div>
						{/if}
						<!-- The range and the pager, in words as well as buttons: "51–100 of 3,412" is what
						     says the list is a part of something larger, which the old capped table never did. -->
						<div class="flex flex-wrap items-center gap-2 text-default text-xs mt-3" aria-live="polite">
							{#if findingsError !== ''}
								<span class="text-red-500">The findings could not be read: {findingsError}</span>
							{:else if isSavedBeforeFindings}
								<span>
									This report was saved before every finding was kept, so it lists the first
									{report.findings.length}. Run the dry run again to page through all of them.
								</span>
							{:else if findingsTotal > 0}
								<span>
									Showing {findingsPageOffset + 1}–{findingsPageOffset + findingsPage.length}
									of {findingsTotal.toLocaleString()}
									{findingSeverity === 'blocking'
										? 'blocking findings'
										: findingSeverity === 'advisory'
											? 'findings to acknowledge'
											: findingSeverity === 'note'
												? 'notes'
												: 'findings'}.
								</span>
							{:else if !findingsLoading}
								<span>Nothing of that kind.</span>
							{/if}
							{#if findingSeverity !== ''}
								<button
									type="button"
									class="underline text-primary-600 dark:text-primary-400"
									on:click={() => (findingSeverity = '')}
								>
									Show every finding
								</button>
							{/if}
							{#if !isSavedBeforeFindings && findingsTotal > FINDINGS_PAGE_SIZE}
								<span class="ml-auto flex gap-2">
									<Button
										size="xs"
										color="alternative"
										disabled={findingsLoading || findingsOffset === 0}
										on:click={() => (findingsOffset = Math.max(0, findingsOffset - FINDINGS_PAGE_SIZE))}
									>
										Previous
									</Button>
									<Button
										size="xs"
										color="alternative"
										disabled={findingsLoading || findingsOffset + FINDINGS_PAGE_SIZE >= findingsTotal}
										on:click={() => (findingsOffset = findingsOffset + FINDINGS_PAGE_SIZE)}
									>
										Next
									</Button>
								</span>
							{/if}
						</div>
						<div class="min-w-0 overflow-x-auto mt-3">
							<Table class="dataTable">
								<TableHead>
									<TableHeadCell>Severity</TableHeadCell>
									<TableHeadCell>What</TableHeadCell>
									<TableHeadCell>Record</TableHeadCell>
									<TableHeadCell><span class="sr-only">Quick fix</span></TableHeadCell>
								</TableHead>
								<TableBody>
									{#each shownFindings as finding, index (`${findingsPageOffset}:${index}:${finding.code}:${finding.sourceID}`)}
										<TableBodyRow>
											<TableBodyCell>
												<Badge color={finding.severity === 'blocking' ? 'red' : finding.severity === 'advisory' ? 'yellow' : 'dark'}>
													{finding.severity}
												</Badge>
											</TableBodyCell>
											<TableBodyCell>{finding.message}</TableBodyCell>
											<TableBodyCell>{finding.sourceKind} {finding.sourceID}</TableBodyCell>
											<TableBodyCell>
												<!-- The same fix as its row under Quick fixes, so it resolves every finding
												     sharing it; the title names the action, since a row's button that
												     changes three hundred rows without saying so would be a surprise. -->
												{#if finding.fix !== undefined}
													{@const fix = finding.fix}
													{#if fixOutcomes[fix.key]?.ok === true}
														<span class="text-xs text-green-600 dark:text-green-400">Fixed, run again</span>
													{:else}
														<Button
															size="xs"
															color="alternative"
															title={fix.label}
															disabled={busy !== '' || applyingFix !== ''}
															on:click={() => applyQuickFix(fix)}
														>
															{applyingFix === fix.key ? 'Applying…' : 'Fix'}
														</Button>
													{/if}
												{/if}
											</TableBodyCell>
										</TableBodyRow>
									{/each}
								</TableBody>
							</Table>
						</div>
					{/if}
				</div>
			{/if}
		</Panel>

		<!-- Keyed on the report, so a fresh dry run reads its own plan rather than the last one's. -->
		{#key `${selected.uuid}:${report?.uuid ?? ''}`}
			<MigrationPlan batchUUID={selected.uuid} />
		{/key}

		<Panel title="4 · Apply" faIcon="fa-database">
			{#if report === null}
				<EmptyState
					variant="empty"
					title="Dry-run it first"
					description="Nothing has checked what applying this batch would do."
				/>
			{:else if report.isBlocked && !acknowledgeBlocking}
				<EmptyState
					variant="empty"
					title="Blocked"
					description="Resolve the blocking findings above and run the assessment again — or acknowledge them below and import anyway."
				/>
				<!--
					The override is a tick rather than a second button, because it has to be *read*.
					Some of these findings are an operator's call; one of them — a subscription
					Braintree is billing that the export does not carry — is a customer charged every
					month with nothing here recording it, and this store cannot tell which is which.
				-->
				<label class="mt-3 flex items-start gap-2 text-sm">
					<input type="checkbox" class="mt-0.5 shrink-0" bind:checked={acknowledgeBlocking} />
					<span>
						Import anyway, accepting {report.blocking} blocking finding(s). Whatever they name
						is not imported: those customers keep no entitlement here, and anything Braintree
						is still billing goes on billing with no record of it in this store. The override
						is recorded in the audit log.
					</span>
				</label>
			{:else}
				<PromptButton
					confirmText={`Import ${report.counts.liveSubscriptions ?? 0} live subscriptions and ${report.counts.transactions ?? 0} transactions?`}
					confirmDetail={report.isBlocked
						? `${report.blocking} blocking finding(s) are being overridden. Nobody is charged and ${acknowledgeListeningApplications ? 'the scoped applications hear each customer as they are imported' : 'no application is notified'}, but whatever those findings name is not imported. Applying cannot be undone: repair afterwards is forward-only.`
						: `Nobody is charged and ${acknowledgeListeningApplications ? 'the scoped applications hear each customer as they are imported' : 'no application is notified until step 6'}. Applying cannot be undone: repair afterwards is forward-only.`}
					disabled={busy !== ''}
					on:confirm={apply}
				>
					{busy === 'apply' ? 'Applying…' : 'Apply'}
				</PromptButton>
			{/if}

			{#if listeningApplications !== null}
				<!--
					A tick for the same reason the blocking override is one: it has to be read. An
					application scoped to these keys adopts each customer's legacy licence the moment it
					hears about it, and from then on the legacy store can no longer renew or restore it.
				-->
				<p class="text-sm mt-3">{listeningApplications}</p>
				<label class="mt-2 flex items-start gap-2 text-sm">
					<input
						type="checkbox"
						class="mt-0.5 shrink-0"
						bind:checked={acknowledgeListeningApplications}
					/>
					<span>
						Deliver to them as each customer is imported. Their licences are taken over as the
						apply runs, before anything has been checked, and an undo afterwards cannot give them
						back to the legacy store. The choice is recorded in the audit log.
					</span>
				</label>
			{/if}

			{#if applied !== null}
				<div class="mt-4 text-sm">
					<p>
						{applied.customers} customer(s), {applied.subscriptions} subscription(s),
						{applied.orders} order(s), {applied.invoices} invoice(s).
					</p>
					{#if applied.failed > 0}
						<p class="text-red-500 mt-1">
							{applied.failed} customer(s) failed and were recorded rather than skipped silently.
						</p>
					{/if}
				</div>
			{/if}
		</Panel>

		{#if selected.appliedAt !== null}
			<Panel title="6 · Notify the applications" faIcon="fa-tower-broadcast">
				<p class="text-default text-sm">
					Sends each consuming application the current state of every entitlement these
					customers hold. The apply itself sends nothing — one delivery per application per
					change would be a flood across a whole customer base — so this is the step that
					seeds them.
				</p>
				<!--
					The half that answers "will a cancellation reach the licence apps". A subscription
					that had already ended in the old store arrives here revoked, and an application
					holding a legacy licence for it has to be *told*: silence reads as "nothing
					changed" and would leave that licence alive for ever.
				-->
				<p class="text-default text-sm mt-2">
					Revoked entitlements are sent too, which is what lets an application cancel a
					licence for a subscription that had already ended.
				</p>

				<Button class="mt-3" disabled={busy !== ''} on:click={notify}>
					{busy === 'notify' ? 'Queueing…' : 'Notify the applications'}
				</Button>

				{#if notified !== null}
					<div class="mt-4 text-sm">
						<p>
							{notified.deliveries} delivery(s) queued for {notified.customers} customer(s){notified.notified.length >
							0
								? `, to ${notified.notified.join(', ')}`
								: ''}.
						</p>
						{#if notified.unreachable.length > 0}
							<!-- Named rather than skipped quietly: "seeded" about a store whose only
							     consumer has no webhook URL reads as every application notified. -->
							<p class="text-amber-500 mt-1">
								{#each notified.unreachable as entry (entry.name)}
									<span class="block">{entry.name} received nothing — {entry.reason}.</span>
								{/each}
							</p>
						{/if}
					</div>
				{/if}
			</Panel>
		{/if}

		{#if selected.appliedAt !== null || reverted !== null}
			<Panel title="Undo the import" faIcon="fa-rotate-left">
				<p class="text-default text-sm">
					Removes every purchase, invoice, refund, grant, trial and document this import
					created, and the tax rates and coupons it added. Nothing this store sold itself is
					touched — a customer who has bought here since keeps that purchase.
				</p>
				<!--
					Said on the screen rather than only in the code, because it is the one thing an
					operator will otherwise assume happened: the accounts stay, and their entitlements
					are revoked rather than deleted. Deleting an entitlement row and re-creating it
					restarts its version at 1, and every conforming consumer then discards the payload
					as stale — so a customer who buys again would be locked out permanently, with
					nothing raised anywhere.
				-->
				<p class="text-default text-sm mt-2">
					Accounts are <strong>kept</strong>, with their access revoked rather than erased —
					deleting an entitlement and re-creating it later would lock that customer out of
					every application for good. What is left is an account with no purchases and no
					access.
				</p>

				{#if informedApplications !== null}
					<p class="text-sm mt-3">{informedApplications}</p>
					<label class="mt-2 flex items-start gap-2 text-sm">
						<input
							type="checkbox"
							class="mt-0.5 shrink-0"
							bind:checked={acknowledgeApplicationsInformed}
						/>
						<span>
							Undo anyway. Those applications revoke these customers' licences as well, and a
							licence the Store has taken over cannot be handed back to the legacy store. The
							choice is recorded in the audit log.
						</span>
					</label>
				{/if}

				<PromptButton
					class="mt-3"
					confirmText="Undo this import?"
					confirmDetail="Every imported purchase, invoice and refund is deleted and every imported entitlement is revoked. Customers lose the access they were migrated with. This cannot itself be undone — the only way back is to import again."
					disabled={busy !== ''}
					on:confirm={revert}
				>
					{busy === 'revert' ? 'Undoing…' : 'Undo the import'}
				</PromptButton>

				{#if reverted !== null}
					<div class="mt-4 text-sm">
						<p>
							Removed {reverted.subscriptions} subscription(s), {reverted.orders} order(s),
							{reverted.invoices} invoice(s), {reverted.refunds} refund(s),
							{reverted.grants} grant(s), {reverted.trials} trial(s),
							{reverted.documents} document(s), {reverted.taxRates} tax rate(s) and
							{reverted.coupons} coupon(s).
						</p>
						<p class="text-default mt-1">
							{reverted.usersRetained} account(s) kept with their access revoked.
						</p>
					</div>
				{/if}
			</Panel>
		{/if}

		<Panel title="5 · Invoice documents" faIcon="fa-file-pdf">
			<p class="text-default text-sm">
				The PDFs the legacy store actually sent, uploaded <strong>after</strong> the apply: each one
				is matched to the invoice carrying its legacy id, and before the apply there are none for
				them to belong to. A second upload of the same archive stores nothing new.
			</p>
			<label class="text-sm block mt-3">
				<span class="card-label">Document archive</span>
				<input
					class="w-full rounded-lg bg-depth border-widget-color text-sm"
					type="file"
					accept=".gz,.ndjson,.jsonl,application/gzip,application/x-ndjson"
					on:change={onDocumentsChosen}
				/>
			</label>
			<Button
				class="mt-3"
				disabled={documentsFile === null || busy !== '' || (documentsSummary?.isRunning === true && !documentsSummary.isStale)}
				on:click={uploadDocuments}
			>
				{busy === 'documents' ? 'Uploading…' : 'Upload documents'}
			</Button>

			{#if documentsSummary !== null}
				<div class="mt-4 text-sm" aria-live="polite">
					{#if documentsSummary.isStale}
						<p class="text-amber-500">
							The last upload stopped being read part-way, after {documentsSummary.read} document(s).
							Upload the archive again — documents already stored are counted, not stored twice.
						</p>
					{:else if documentsSummary.isRunning}
						<p class="flex items-center gap-2">
							<Spinner size="4" />
							Reading the archive — {documentsSummary.read} document(s) so far.
						</p>
					{:else if documentsSummary.phase === 'failed'}
						<p class="text-red-500">The upload failed after {documentsSummary.read} document(s): {documentsSummary.error}</p>
					{/if}
					<p>
						{documentsSummary.stored} stored, {documentsSummary.alreadyHeld} already held, of
						{documentsSummary.read} read.
					</p>
					{#if documentsSummary.unmatchedCount > 0}
						<!--
							Everything unmatched means the archive was uploaded against the wrong provider
							or before the apply. Stated rather than reported as a quiet success, which is
							what an operator would otherwise read a 200 as.
						-->
						<p class="text-amber-500 mt-1">
							{documentsSummary.unmatchedCount} document(s) matched no migrated invoice
							{#if documentsSummary.unmatched.length > 0}
								— {documentsSummary.unmatched.join(', ')}{documentsSummary.unmatchedCount >
								documentsSummary.unmatched.length
									? ', …'
									: ''}
							{/if}
						</p>
					{/if}
					{#if documentsSummary.rejected.length > 0}
						<p class="text-red-500 mt-1">
							{documentsSummary.rejected.length} document(s) were refused:
							{documentsSummary.rejected.map(row => `${row.sourceID} (${row.reason})`).join('; ')}
						</p>
					{/if}
				</div>
			{/if}
		</Panel>
	{/if}
</div>
